import { create } from 'zustand';
import type {
  MultiViewFilterClause,
  MultiViewLinkMode,
  MultiViewSelectionState,
  MultiViewSharedQueryState,
} from '../core/multi-view';

export type MultiCanvasProjectionKind = string;

export type MultiCanvasLayout =
  | 'split_50_50'
  | 'split_75_25'
  | 'split_25_75'
  | 'custom';

const MIN_SPLIT_RATIO = 0.2;
const MAX_SPLIT_RATIO = 0.8;

function clampSplitRatio(value: number): number {
  if (!Number.isFinite(value)) return 0.5;
  return Math.min(MAX_SPLIT_RATIO, Math.max(MIN_SPLIT_RATIO, value));
}

function ratioForLayout(layout: MultiCanvasLayout): number {
  if (layout === 'split_75_25') return 0.75;
  if (layout === 'split_25_75') return 0.25;
  return 0.5;
}

export interface MultiCanvasProjectionState {
  readonly kind: MultiCanvasProjectionKind;
  readonly openedAt: number;
  readonly viewpointId?: string;
}

interface MultiCanvasViewState {
  activeProjection: MultiCanvasProjectionState | null;
  layout: MultiCanvasLayout;
  splitRatio: number;
  linkMode: MultiViewLinkMode;
  sharedQuery: MultiViewSharedQueryState;
  selection: MultiViewSelectionState | null;
  openProjection: (kind: MultiCanvasProjectionKind, viewpointId?: string) => void;
  closeProjection: () => void;
  setProjectionViewpoint: (viewpointId: string) => void;
  setLayout: (layout: MultiCanvasLayout) => void;
  setSplitRatio: (ratio: number) => void;
  setLinkMode: (mode: MultiViewLinkMode) => void;
  setSharedQuery: (query: MultiViewSharedQueryState) => void;
  updateSharedQuery: (patch: Partial<MultiViewSharedQueryState>) => void;
  setSharedSearch: (search: string) => void;
  setSharedFilters: (filters: readonly MultiViewFilterClause[]) => void;
  clearSharedQuery: () => void;
  setSelection: (selection: MultiViewSelectionState | null) => void;
  clearSelection: () => void;
}

export const useMultiCanvasViewStore = create<MultiCanvasViewState>((set) => ({
  activeProjection: null,
  layout: 'split_50_50',
  splitRatio: 0.5,
  linkMode: 'linked-selection',
  sharedQuery: {},
  selection: null,
  openProjection: (kind, viewpointId) => set({
    activeProjection: {
      kind,
      openedAt: Date.now(),
      ...(viewpointId ? { viewpointId } : {}),
    },
  }),
  closeProjection: () => set({
    activeProjection: null,
    selection: null,
  }),
  setProjectionViewpoint: (viewpointId) => set((state) => ({
    activeProjection: state.activeProjection
      ? { ...state.activeProjection, viewpointId }
      : null,
  })),
  setLayout: (layout) => set({ layout, splitRatio: ratioForLayout(layout) }),
  setSplitRatio: (ratio) => set({ layout: 'custom', splitRatio: clampSplitRatio(ratio) }),
  setLinkMode: (mode) => set({ linkMode: mode }),
  setSharedQuery: (query) => set({ sharedQuery: normalizeSharedQuery(query) }),
  updateSharedQuery: (patch) => set((state) => ({
    sharedQuery: normalizeSharedQuery({
      ...state.sharedQuery,
      ...patch,
    }),
  })),
  setSharedSearch: (search) => set((state) => ({
    sharedQuery: normalizeSharedQuery({
      ...state.sharedQuery,
      search,
    }),
  })),
  setSharedFilters: (filters) => set((state) => ({
    sharedQuery: normalizeSharedQuery({
      ...state.sharedQuery,
      filters,
    }),
  })),
  clearSharedQuery: () => set({ sharedQuery: {} }),
  setSelection: (selection) => set({ selection }),
  clearSelection: () => set({ selection: null }),
}));

function normalizeSharedQuery(query: MultiViewSharedQueryState): MultiViewSharedQueryState {
  const search = query.search?.trim();
  const filters = query.filters?.filter((filter) => filter.id && filter.field);
  const facets = query.facets && Object.keys(query.facets).length > 0
    ? query.facets
    : undefined;
  const time = query.time && Object.values(query.time).some((value) => value != null && value !== '')
    ? query.time
    : undefined;
  const target = query.target?.id ? query.target : undefined;

  return {
    ...(search ? { search } : {}),
    ...(filters && filters.length > 0 ? { filters } : {}),
    ...(facets ? { facets } : {}),
    ...(time ? { time } : {}),
    ...(target ? { target } : {}),
  };
}
